import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { HOST } from 'config/config';

// =====================
// Query Hooks
// =====================

/**
 * Fetch all posts for a submission detail
 * @param {string} submissionDetailId - The submission detail ID
 */
export const usePostsBySubmissionDetail = (submissionDetailId, options = {}) => {
  return useQuery({
    queryKey: ['posts', 'submissionDetail', submissionDetailId],
    queryFn: async () => {
      const response = await axios.get(`${HOST}/api/certification-tool/posts/submission-detail/${submissionDetailId}`);
      return response.data;
    },
    enabled: !!submissionDetailId,
    staleTime: 1000 * 60, // 1 minute
    ...options,
  });
};

/**
 * Fetch a single post by ID
 * @param {string} postId - The post ID
 */
export const usePost = (postId, options = {}) => {
  return useQuery({
    queryKey: ['posts', postId],
    queryFn: async () => {
      const response = await axios.get(`${HOST}/api/certification-tool/posts/${postId}`);
      return response.data;
    },
    enabled: !!postId,
    staleTime: 1000 * 60, // 1 minute
    ...options,
  });
};

// =====================
// Mutation Hooks
// =====================

/**
 * Create a new post
 */
export const useCreatePost = submissionId => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async postData => {
      const response = await axios.post(`${HOST}/api/certification-tool/posts`, postData);
      return response.data;
    },
    onSuccess: (data, variables) => {
      // Invalidate posts for the submission detail
      if (variables.submissionDetail_Id) {
        queryClient.invalidateQueries({
          queryKey: ['posts', 'submissionDetail', variables.submissionDetail_Id],
        });
      }
      // Invalidate submission to refresh post count in submission details
      if (submissionId) {
        queryClient.invalidateQueries({
          queryKey: ['submission', submissionId],
        });
      }
    },
  });
};

/**
 * Update an existing post
 */
export const useUpdatePost = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ postId, patchDocument }) => {
      const response = await axios.patch(`${HOST}/api/certification-tool/posts/${postId}`, patchDocument);
      return response.data;
    },
    onSuccess: (data, variables) => {
      // Invalidate the specific post
      queryClient.invalidateQueries({
        queryKey: ['posts', variables.postId],
      });
      // Invalidate posts list
      queryClient.invalidateQueries({
        queryKey: ['posts', 'submissionDetail'],
      });
    },
  });
};

/**
 * Delete a post
 */
export const useDeletePost = submissionId => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async postId => {
      const response = await axios.delete(`${HOST}/api/certification-tool/posts/${postId}`);
      return response.data;
    },
    onSuccess: () => {
      // Invalidate all post queries
      queryClient.invalidateQueries({
        queryKey: ['posts'],
      });
      // Invalidate submission to refresh in UI
      if (submissionId) {
        queryClient.invalidateQueries({
          queryKey: ['submission', submissionId],
        });
      }
    },
  });
};

/**
 * Create a reaction on a post
 */
export const useCreatePostReaction = submissionId => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async reactionData => {
      const response = await axios.post(`${HOST}/api/certification-tool/post-reactions`, reactionData);
      return response.data;
    },
    onSuccess: (data, variables) => {
      // Invalidate the post to refresh reactions
      if (variables.post_Id) {
        queryClient.invalidateQueries({
          queryKey: ['posts', variables.post_Id],
        });
      }
      queryClient.invalidateQueries({
        queryKey: ['posts', 'submissionDetail'],
      });
      // Invalidate submission to refresh in UI
      if (submissionId) {
        queryClient.invalidateQueries({
          queryKey: ['submission', submissionId],
        });
      }
    },
  });
};

/**
 * Delete a post reaction
 */
export const useDeletePostReaction = submissionId => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async reactionId => {
      const response = await axios.delete(`${HOST}/api/certification-tool/post-reactions/${reactionId}`);
      return response.data;
    },
    onSuccess: () => {
      // Invalidate all post queries to refresh reactions
      queryClient.invalidateQueries({
        queryKey: ['posts'],
      });
      // Invalidate submission to refresh in UI
      if (submissionId) {
        queryClient.invalidateQueries({
          queryKey: ['submission', submissionId],
        });
      }
    },
  });
};
